<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { filterStore } from "module/store/FilterStore";
  import { FilterType, type SearchFilter } from "module/store/Filters";
  import { getLabels } from "./filterUtils";
  import { loc, mloc } from "module/utils";

  let { selected, types = [] }: { selected?: string; types?: FilterType[] } =
    $props();

  const dispatch = createEventDispatcher();
  const select = (id: string) => () => dispatch("selectFilter", { id });

  function getIcon(filter: SearchFilter) {
    switch (filter.type) {
      case FilterType.Client:
        return "fa-user";
      case FilterType.World:
        return "fa-globe";
      case FilterType.Default:
        return "fa-lock";
    }
  }

  function getTypeName(filter: SearchFilter) {
    switch (filter.type) {
      case FilterType.Client:
        return mloc("FilterListFilterScopeClient");
      case FilterType.World:
        return mloc("FilterListFilterScopeWorld");
      case FilterType.Default:
        return mloc("FilterListFilterScopeDefault");
    }
  }

  function getFilterConfigLabels(filter: SearchFilter) {
    const labels = Object.values(getLabels(filter.filterConfig));
    return labels.length ? labels : ["Unfiltered!"];
  }

  function getRoleName(role: SearchFilter["role"]) {
    switch (role) {
      case CONST.USER_ROLES.PLAYER:
        return loc("USER.RolePlayer");
      case CONST.USER_ROLES.TRUSTED:
        return loc("USER.RoleTrusted");
      case CONST.USER_ROLES.ASSISTANT:
        return loc("USER.RoleAssistant");
      case CONST.USER_ROLES.GAMEMASTER:
        return loc("USER.RoleGamemaster");
    }
  }
</script>

<section class:hidden={selected} class="standard-form">
  {#each Object.values($filterStore)
    .reverse()
    .filter((f) => types.includes(f.type)) as filter (filter.id)}
    <div
      class="filter"
      class:disabled={filter.disabled}
      on:click|preventDefault={select(filter.id)}
    >
      <i class="fas {getIcon(filter)} filter-icon" title={getTypeName(filter)}
      ></i>
      <div class="main">
        <span>
          {filter.subTitle}
        </span>
        <span class="hint">
          <span class="tag">
            @{filter.tag}
          </span>
          {#if filter.type === FilterType.Default}
            <span> {getTypeName(filter)}</span>
          {/if}
          {#each getFilterConfigLabels(filter) as label (label)}
            <span class="config-label">{label}</span>
          {/each}
          {filter.type === FilterType.World ? getRoleName(filter.role) : ""}
        </span>
      </div>
      <div class="extra">
        <button
          on:click|stopPropagation|preventDefault={() =>
            dispatch("toggle", filter.id)}
          title={filter.disabled
            ? mloc("FilterListOptionEnable")
            : mloc("FilterListOptionDisable")}
        >
          <i class="fas {filter.disabled ? 'fa-eye-slash' : 'fa-eye'}"></i>
        </button>
        <button
          on:click|stopPropagation|preventDefault={() =>
            dispatch("clone", filter.id)}
          title={loc("Duplicate")}
        >
          <i class="fas fa-clone"></i>
        </button>
        {#if filter.type !== FilterType.Default}
          <button
            on:click|stopPropagation|preventDefault={() =>
              dispatch("delete", filter.id)}
            title={loc("Delete")}
          >
            <i class="fas fa-trash"></i>
          </button>
        {/if}
      </div>
    </div>
  {/each}
</section>

<style>
  .hidden {
    display: none;
  }
  section {
    display: flex;
    height: 100%;
    overflow: auto;
    flex-direction: column;
    padding: 0.7em;
  }
  .filter {
    display: flex;
    border: 2px solid var(--color-light-6);
    border-radius: 10px;
    padding: 0.5em;
    cursor: pointer;
  }
  .filter:hover {
    color: var(--color-text-primary);
    border-color: var(--color-light-5);
  }
  .filter:focus {
    outline: 3px solid var(--color-warm-2);
    outline-offset: -3px;
  }

  .filter.disabled {
    opacity: 0.8;
    border-style: dotted;
    pointer-events: all;
  }
  .main {
    word-wrap: break-word;
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
  }
  .hint {
    width: 100%;
  }
  .extra {
    font-size: 120%;
    flex: 0 0 85px;
    display: flex;
    justify-content: space-around;
    align-items: center;
  }

  .extra button {
    display: flex;
    height: 24px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 24px;
    border: none;
  }
  .extra button i {
    margin: 0;
  }

  .tag {
    background: var(--qiFilterAccent);
    color: #f0f0e0;
    border-radius: 3px;
    padding: 1px 2px;
  }

  .config-label {
    background-color: #0003;
    border-radius: 4px;
    padding: 0 4px;
    white-space: nowrap;
    margin-right: 3px;
  }
  .filter-icon {
    display: flex;
    align-items: center;
    padding: 0.4em;
    font-size: 150%;
    opacity: 0.9;
  }
</style>
